import { useMemo, type ReactNode } from "react";
import { Link, Navigate, Outlet, useParams } from "react-router-dom";
import { isPrivateSection, type SpaceSection } from "@al-life/types";
import { useAuth } from "@/features/auth/AuthContext";
import { FEATURES } from "@/features/registry";
import { SpaceContext, useSpaceContext, type SpaceContextValue } from "./SpaceContext";
import { useSpace } from "./useSpace";
import { SpaceSectionBar } from "./SpaceSectionBar";
import styles from "./spaces.module.css";

/**
 * Wraps every `/:slug/...` route. Loads the space and provides SpaceContext to
 * the subtree (via <Outlet/>). Shows a loading state while fetching and a
 * not-found state for an unknown slug. Access to individual sections is enforced
 * by <RequireSpaceAccess> (below), not here — the space home is viewable by
 * anyone who can see at least the shell.
 */
export function SpaceProvider() {
  const { slug = "" } = useParams();
  const { space, status, reload } = useSpace(slug);

  const value = useMemo<SpaceContextValue>(
    () => ({
      slug,
      space,
      canEdit: space?.canEdit ?? false,
      isPublicSection: (section: SpaceSection) => space?.publicSections.includes(section) ?? false,
      reload,
    }),
    [slug, space, reload]
  );

  if (status === "loading") {
    return <div className={styles.center} aria-busy="true">Loading…</div>;
  }
  if (status === "notfound") {
    return (
      <div className={styles.center}>
        <p className={styles.notFoundEmoji} aria-hidden="true">🧭</p>
        <p>That space doesn't exist.</p>
      </div>
    );
  }
  if (status === "error" || !space) {
    return <div className={styles.center}>Couldn't load this space. Please try again.</div>;
  }

  return (
    <SpaceContext.Provider value={value}>
      <Outlet />
    </SpaceContext.Provider>
  );
}

/**
 * Gate a section within a space. Renders `children` only when the caller may
 * read `section` (it's public, or they're a member). Otherwise:
 *  - private-by-design section (the diary) → an explainer of what it is, never
 *    the data — regardless of login state (there's nothing to unlock unless
 *    you're a member of THIS space).
 *  - anonymous → redirect to /login?next=<path>
 *  - logged-in non-member → a friendly "private" notice.
 * Must be used inside a SpaceProvider.
 */
export function RequireSpaceAccess({
  section,
  children,
}: {
  section: SpaceSection;
  children: ReactNode;
}) {
  const { status: authStatus } = useAuth();
  const { space, isPublicSection, canEdit } = useSpaceContext();

  // The allow decision needs only the (possibly cached) space doc, so allowed
  // viewers render immediately — auth is awaited only on the deny path, where
  // we must know anonymous-vs-member to pick redirect vs explainer.
  if (!space) {
    return <div className={styles.center} aria-busy="true">Loading…</div>;
  }

  // A private-by-design section can never be opened by publicSections — mirror
  // the server-side rule so stale data can't leak anything client-side either.
  const allowed = canEdit || (!isPrivateSection(section) && isPublicSection(section));
  if (allowed) {
    return (
      <>
        <SpaceSectionBar section={section} />
        {children}
      </>
    );
  }

  if (authStatus === "loading") {
    return <div className={styles.center} aria-busy="true">Loading…</div>;
  }

  if (isPrivateSection(section)) {
    return <PrivateSectionExplainer section={section} spaceName={space.name} showLogin={authStatus === "anonymous"} />;
  }

  if (authStatus === "anonymous") {
    const next = encodeURIComponent(window.location.pathname);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  // Logged in but not a member and the section isn't public.
  return (
    <div className={styles.center}>
      <p className={styles.notFoundEmoji} aria-hidden="true">🔒</p>
      <p>This section is private.</p>
    </div>
  );
}

/**
 * What a non-member sees on a private-by-design section: what the feature IS
 * (copy reused from the feature registry), never the space's real data.
 */
function PrivateSectionExplainer({
  section,
  spaceName,
  showLogin,
}: {
  section: SpaceSection;
  spaceName: string;
  showLogin: boolean;
}) {
  const feature = FEATURES.find((f) => f.section === section);
  return (
    <div className={styles.center}>
      <p className={styles.notFoundEmoji} aria-hidden="true">{feature?.emoji ?? "🔒"}</p>
      {feature && <h2 className={styles.explainerTitle}>{feature.title}</h2>}
      {feature && <p className={styles.explainerBlurb}>{feature.description}</p>}
      <p className={styles.explainerPrivate}>
        🔒 This section is always private — only <strong>{spaceName}</strong>'s members can see
        what's inside.
      </p>
      {showLogin && (
        <Link className={styles.explainerLogin} to={`/login?next=${encodeURIComponent(window.location.pathname)}`}>
          Log in
        </Link>
      )}
    </div>
  );
}
